import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import type { PublicFiliereTrack } from '../../utils/publicFilieres';
import { resolveTrackCoverImage } from '../../utils/trackCoverImage';
import '../../styles/publicFiliereCarousel.css';

type Props = {
  tracks: PublicFiliereTrack[];
  eyebrow: string;
  title: string;
  subtitle: string;
};

function trackDescription(track: PublicFiliereTrack): string {
  const intro = track.intro?.trim();
  if (intro) return intro;
  const names = track.filieres.map((f) => f.libelle).slice(0, 3);
  if (names.length === 0) return 'Formations professionnelles certifiantes du centre JCONSULT MY Academia.';
  const suffix = track.filieres.length > 3 ? ` et ${track.filieres.length - 3} autre(s).` : '.';
  return `${names.join(', ')}${suffix}`;
}

export default function PublicFiliereTracksCarousel({ tracks, eyebrow, title, subtitle }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const syncNav = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollBack(el.scrollLeft > 4);
    setCanScrollForward(el.scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    syncNav();
    const el = trackRef.current;
    if (!el) return undefined;

    el.addEventListener('scroll', syncNav, { passive: true });
    const ro = new ResizeObserver(syncNav);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', syncNav);
      ro.disconnect();
    };
  }, [syncNav, tracks.length]);

  const scrollByPage = (direction: -1 | 1) => {
    const el = trackRef.current;
    if (!el) return;
    // Une « page » = tout le viewport du track (1 carte mobile, 3 cartes dès md)
    el.scrollBy({ left: direction * el.clientWidth, behavior: 'smooth' });
  };

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-widest text-primary">{eyebrow}</p>
          <h2 className="mt-2 text-3xl font-black text-slate-900 sm:text-4xl">{title}</h2>
          <p className="mt-3 text-on-surface-variant">{subtitle}</p>
        </div>
        {tracks.length > 1 ? (
          <div className="public-filiere-carousel__nav shrink-0" aria-label="Navigation du carrousel">
            <button
              type="button"
              className="public-filiere-carousel__nav-btn"
              onClick={() => scrollByPage(-1)}
              disabled={!canScrollBack}
              aria-label="Filière précédente"
            >
              <LucideSymbolIcon name="arrow_back" tone="inherit" size={20} />
            </button>
            <button
              type="button"
              className="public-filiere-carousel__nav-btn"
              onClick={() => scrollByPage(1)}
              disabled={!canScrollForward}
              aria-label="Filière suivante"
            >
              <LucideSymbolIcon name="arrow_forward" tone="inherit" size={20} />
            </button>
          </div>
        ) : null}
      </div>

      {tracks.length > 0 ? (
        <div className="public-filiere-carousel mt-10">
          <div
            ref={trackRef}
            className="public-filiere-carousel__track"
            role="list"
            aria-label="Domaines de formation"
          >
            {tracks.map((track) => {
              const coverImage = resolveTrackCoverImage(track);
              return (
              <article key={track.slug} className="public-filiere-carousel__slide" role="listitem">
                <div className="public-filiere-resource-card">
                  <div className="public-filiere-resource-card__media">
                    <img
                      src={coverImage}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="public-filiere-resource-card__content">
                    <p className="public-filiere-resource-card__eyebrow">
                      {track.filieres.length} formation{track.filieres.length > 1 ? 's' : ''}
                    </p>
                    <h3 className="public-filiere-resource-card__title">{track.title}</h3>
                    <p className="public-filiere-resource-card__body">{trackDescription(track)}</p>
                    <Link to={`/filieres/${track.slug}`} className="public-filiere-resource-card__link">
                      En savoir plus
                      <LucideSymbolIcon name="arrow_forward" tone="inherit" size={16} />
                    </Link>
                  </div>
                </div>
              </article>
            );
            })}
          </div>
        </div>
      ) : null}
    </>
  );
}
